import { Stack } from '@/components/layout';
import { AppHeader } from '@/components/layout/app-header';
import { PlayerListTemplateV2 } from '@/components/template/player-list';
import { SvgIcon, Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { useDashboard } from '@/features/home/hooks/use-dashboard';
import {
  NewPlayerButtonMobile,
  NewPlayerButtonTablet,
} from '@/features/player-session/components/overview-session/add-tracking-button';
import { SelectPlayerCard } from '@/features/player-session/components/select-player/select-player-card';
import { useSelectPlayer } from '@/features/player-session/hooks/use-select-player';
import { createStyles, useScaling } from '@/hooks';
import { HomeStackParamList } from '@/navigations/types';
import { useSessionStore } from '@/stores';
import { colors } from '@/theme/colors';
import { StackActions, useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import React, { useCallback } from 'react';
import { StyleSheet, TouchableOpacity } from 'react-native';

export const SelectPlayerScreen = () => {
  const userUid = useSessionStore(s => s.user?.uid);

  return (
    <PlayerListTemplateV2.Providers userUid={userUid}>
      <SelectPlayerScreenContent />
    </PlayerListTemplateV2.Providers>
  );
};

function SelectPlayerScreenContent() {
  const { scale, isTablet } = useScaling();
  const styles = useStyles();
  const { hasActiveSession } = useDashboard();
  const navigation = useNavigation<NativeStackNavigationProp<HomeStackParamList>>();
  const {
    selectedIds,
    selectedCount,
    titleResultsSummary,
    isRightActionDisabled,
    onToggleChecked,
    startContinue,
    listPlayerLogic,
  } = useSelectPlayer({ limit: 50 });

  const renderItem = useCallback(
    ({ item }: { item: player.PlayerLookupData }) => (
      <SelectPlayerCard
        player={item}
        checked={selectedIds.has(item.id)}
        onToggleChecked={onToggleChecked}
      />
    ),
    [selectedIds, onToggleChecked],
  );

  const onGoBack = useCallback(() => {
    navigation.goBack();
  }, [navigation]);

  const onPressRightAction = useCallback(async () => {
    const sessionId = await startContinue();
    if (!sessionId) return;
    requestIdleCallback(() => {
      hasActiveSession ? navigation.goBack() : navigation.replace('OverviewSession');
    });
  }, [hasActiveSession, navigation, startContinue]);

  const onCreatePlayer = useCallback(() => {
    navigation.dispatch(
      StackActions.push('MutatePlayer', {
        mode: 'create',
        deleteReturnHomeScreen: 'SelectPlayer',
      }),
    );
  }, [navigation]);

  return (
    <PlayerListTemplateV2.Root
      listLogic={listPlayerLogic}
      loadingText={STRINGS.player.listPlayers.loadingPlayers}
      skipHideActionButtonOnScroll>
      <PlayerListTemplateV2.Header>
        <AppHeader backgroundColor="white" style={styles.header}>
          <AppHeader.Left>
            <TouchableOpacity activeOpacity={0.6} onPress={onGoBack}>
              <Stack direction="row" align="center" gap={scale(2)} width={scale(70)}>
                <SvgIcon name="IcBackBlue" size={scale(24)} />
                <Text
                  variant={
                    isTablet ? 'Font Inter/Subtitle/Medium 14px' : 'Font Inter/Subtitle/Medium'
                  }
                  style={styles.backText}>
                  {STRINGS.playerSession.selectPlayers.back}
                </Text>
              </Stack>
            </TouchableOpacity>
          </AppHeader.Left>

          <AppHeader.Title>
            <Text
              variant={
                isTablet
                  ? 'Font Inter/Subtitle/Bold 14px'
                  : 'Font Montserrat/Subtitle/Subtitle 2 Bold 12px'
              }
              transform="uppercase"
              fontWeight={isTablet ? undefined : '800'}
              style={styles.headerTitle}>
              {STRINGS.playerSession.selectPlayers.headerTitle}
            </Text>
          </AppHeader.Title>

          <AppHeader.Right>
            <TouchableOpacity
              disabled={isRightActionDisabled}
              activeOpacity={isRightActionDisabled ? 1 : 0.7}
              style={[styles.rightAction, isRightActionDisabled && styles.rightActionDisabled]}
              onPress={onPressRightAction}>
              <Stack direction="row" justify="flex-end" align="center" gap={scale(2)}>
                <Text
                  variant={
                    isTablet ? 'Font Inter/Subtitle/Medium 14px' : 'Font Inter/Subtitle/Medium'
                  }
                  style={[
                    styles.backText,
                    isRightActionDisabled && styles.rightActionTextDisabled,
                  ]}>
                  {hasActiveSession
                    ? STRINGS.playerSession.actions.resume
                    : STRINGS.playerSession.actions.start}
                </Text>
                <SvgIcon
                  name="IcEdit"
                  size={scale(24)}
                  color={isRightActionDisabled ? colors['5'] : colors.Blue}
                />
              </Stack>
            </TouchableOpacity>
          </AppHeader.Right>
        </AppHeader>
      </PlayerListTemplateV2.Header>

      <PlayerListTemplateV2.Toolbar titleResultsSummary={titleResultsSummary}>
        <PlayerListTemplateV2.SearchBar />
        <PlayerListTemplateV2.Filter />
        <PlayerListTemplateV2.Pagination />
        <PlayerListTemplateV2.ActionButton
          mobile={
            <NewPlayerButtonMobile
              leftText={STRINGS.playerSession.actions.selectedSummaryCount(selectedCount)}
              onPress={onCreatePlayer}
            />
          }
          tablet={<NewPlayerButtonTablet onPress={onCreatePlayer} />}
        />
      </PlayerListTemplateV2.Toolbar>

      <PlayerListTemplateV2.List
        renderItem={renderItem}
        // drawDistance={2000}
        numberOfColumns={{ phone: 1, tablet: 3 }}
        gap={scale({ phone: 12, tablet: 16 })}
        extraData={selectedCount}
      />
    </PlayerListTemplateV2.Root>
  );
}

const useStyles = createStyles(() => ({
  header: {
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors['10'],
  },
  headerTitle: {
    color: colors.Blue,
  },
  backText: {
    color: colors.Blue,
  },
  rightAction: {
    opacity: 1,
  },
  rightActionDisabled: {
    opacity: 0.4,
  },
  rightActionTextDisabled: {
    opacity: 0.7,
  },
}));
